import React, { useEffect, useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { useLanguage } from '../contexts/LanguageContext';
import { formatTourDate, parseISODate, toISODate, localeFor } from '@/lib/utils';
import { tr, enGB, de, es, zhCN, arSA } from 'date-fns/locale';
import { trackEvent } from '@/lib/analytics';

const DATE_FNS_LOCALES = { tr, en: enGB, de, es, zh: zhCN, ar: arSA };

const STATUS_STYLES = {
  available: 'bg-green-100 text-green-700',
  limited: 'bg-amber-100 text-amber-700',
  full: 'bg-red-100 text-red-700',
};

const DepartureRow = ({ dep, selected, onSelect }) => {
  const { language, t } = useLanguage();
  const label = { available: t('tour.available'), limited: t('tour.limited'), full: t('tour.full') }[dep.availability];
  return (
    <button
      type="button"
      disabled={!dep.bookable}
      onClick={() => onSelect(dep.date)}
      data-testid={`departure-${dep.date}`}
      className={`w-full flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors ${
        selected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:border-orange-300'
      } ${dep.bookable ? '' : 'opacity-60 cursor-not-allowed'}`}
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 break-words">
          {formatTourDate(dep.date, language)}
          {dep.end_date && dep.end_date !== dep.date && ` – ${formatTourDate(dep.end_date, language, { day: 'numeric', month: 'long' })}`}
        </p>
        {dep.bookable && <p className="text-xs text-gray-500">{dep.remaining} {t('tour.seats_left')}</p>}
      </div>
      <span className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[dep.availability]}`} data-testid={`departure-status-${dep.date}`}>
        {label}
      </span>
    </button>
  );
};

export const TourDepartures = ({ departures, selectedDate, onSelect, infoRequestUrl }) => {
  const { language, t } = useLanguage();
  const [month, setMonth] = useState(null);
  useEffect(() => { if (selectedDate) setMonth(parseISODate(selectedDate)); }, [selectedDate]);
  const visible = departures.filter(d => d.availability !== 'closed');
  if (visible.length === 0) {
    return (
      <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-600" data-testid="tour-no-open-dates">
        <p className="font-semibold text-gray-800 mb-1">{t('tour.dates_soon')}</p>
        <p>{t('tour.no_open_dates')}</p>
        {infoRequestUrl && (
          <a href={infoRequestUrl} target="_blank" rel="noopener noreferrer" data-testid="tour-info-request-button" data-track="info_request"
            onClick={() => trackEvent('info_request', { tour_slug: window.location.pathname.split('/')[2] })}
            className="mt-3 inline-flex items-center justify-center rounded-full bg-green-500 px-4 py-2 font-semibold text-white hover:bg-green-600 transition-colors">
            {t('tour.info_request')}
          </a>
        )}
      </div>
    );
  }
  const byAvailability = (key) => visible.filter(d => d.availability === key).map(d => parseISODate(d.date));
  const bookable = new Set(visible.filter(d => d.bookable).map(d => d.date));
  return (
    <div className="space-y-4" data-testid="tour-departures">
      <div>
        <h3 className="text-base font-bold text-gray-900 mb-2">{t('tour.upcoming_dates')}</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {visible.map(dep => <DepartureRow key={dep.date} dep={dep} selected={selectedDate === dep.date} onSelect={onSelect} />)}
        </div>
      </div>
      <div className="flex justify-center rounded-xl border border-gray-100 bg-white">
        <Calendar
          mode="single"
          locale={DATE_FNS_LOCALES[language] || tr}
          weekStartsOn={1}
          selected={selectedDate ? parseISODate(selectedDate) : undefined}
          onSelect={(date) => date && onSelect(toISODate(date))}
          month={month || parseISODate(visible[0].date)}
          onMonthChange={setMonth}
          disabled={(date) => !bookable.has(toISODate(date))}
          modifiers={{ available: byAvailability('available'), limited: byAvailability('limited'), full: byAvailability('full') }}
          modifiersClassNames={{
            available: 'bg-green-100 text-green-800 font-bold',
            limited: 'bg-amber-100 text-amber-800 font-bold',
            full: 'bg-red-50 text-red-400 line-through',
          }}
          data-testid="tour-departure-calendar"
        />
      </div>
      <p className="text-xs text-gray-500">{t('tour.pick_date_hint')}</p>
    </div>
  );
};

export const TourDateCards = ({ departures, selectedDate, onSelect }) => {
  const { language, t } = useLanguage();
  const visible = departures.filter(d => d.availability !== 'closed');
  if (visible.length === 0) return null;
  const labels = { available: t('tour.available'), limited: t('tour.limited'), full: t('tour.full') };
  return (
    <div className="bg-white rounded-2xl p-8 border border-gray-100" data-testid="tour-dates-section">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('tour.dates_section')}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {visible.map(dep => {
          const start = parseISODate(dep.date);
          return (
            <button
              key={dep.date}
              type="button"
              disabled={!dep.bookable}
              onClick={() => onSelect(dep.date)}
              data-testid={`tour-date-card-${dep.date}`}
              className={`flex items-center gap-4 rounded-xl border p-3 text-left transition-colors ${
                selectedDate === dep.date ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'
              } ${dep.bookable ? '' : 'opacity-60 cursor-not-allowed'}`}
            >
              <div className="bg-orange-500 text-white rounded-lg px-3 py-2 text-center min-w-[64px]">
                <div className="text-2xl font-bold font-ui leading-none">{start.getDate()}</div>
                <div className="text-xs uppercase mt-1">{start.toLocaleDateString(localeFor(language), { month: 'short' })}</div>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 break-words">
                  {formatTourDate(dep.date, language)}
                  {dep.end_date && dep.end_date !== dep.date && ` – ${formatTourDate(dep.end_date, language, { day: 'numeric', month: 'long' })}`}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[dep.availability]}`}>{labels[dep.availability]}</span>
                  {dep.bookable && <span className="text-xs text-gray-500">{dep.remaining} {t('tour.seats_left')}</span>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
